/**
 * Page registry. Every folder in src/pages/ with an index.tsx that exports
 * `pages: PageDef[]` becomes routes under the signed-in app shell, and pages
 * with a `nav` entry appear in the sidebar. Adding an area never touches the
 * shared router files. See docs/web.md, "Adding a page".
 *
 * A module may also export `publicPages: PageDef[]`: routes that work signed
 * out (RA directory, sign-up). The router mounts them outside the sign-in
 * guard; see router.tsx for the layout they get.
 */
import type { RouteComponent } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import type { ComponentType } from "react";

export const NAV_GROUPS = [
  "Markets",
  "Research",
  "Premium",
  "AI",
  "Analysts",
  "Practice",
  "System",
] as const;
export type NavGroup = (typeof NAV_GROUPS)[number];

export interface NavEntry {
  label: string;
  icon: LucideIcon;
  group: NavGroup;
  /** Position within the group, lowest first. */
  order?: number;
  /** A small count after the label (e.g. new AI calls); renders nothing when there is none. */
  badge?: ComponentType;
  /** Stands out in the menus (the "<brand> AI" entry): a coloured icon and label. */
  highlight?: boolean;
}

export interface PageDef {
  /** TanStack Router path, e.g. "/app/market" or "/app/stocks/$symbol". */
  path: string;
  /** Shown in the top bar and the browser tab. */
  title: string;
  /** Prefer `lazyRouteComponent(() => import("./Page"), "Page")` so each area is its own chunk. */
  component: RouteComponent;
  /** Omit for detail pages that are reached by links, not the sidebar. */
  nav?: NavEntry;
  /** Parse and default the URL search params (filters, tabs) — keep them in the URL. */
  validateSearch?: (search: Record<string, unknown>) => object;
  /** Hide from everyone but admins (stored role "owner" or "admin"). */
  ownerOnly?: boolean;
  /**
   * Show in the sidebar only to these roles (stored role or platform role:
   * "admin" also covers "owner", "investor" also covers "subscriber"). The
   * server enforces access on its own; this only tidies the nav.
   */
  roles?: string[];
  /**
   * AI calls and candidates: shown to admins and to users an admin has ticked
   * for AI calls (`ai_calls_access`). The server enforces it too.
   */
  aiAccess?: boolean;
  /**
   * Only admins and users ticked for AI & calls (`ai_calls_access`, the
   * "friends"). Everyone else doesn't see it in the nav, and opening the URL
   * goes to Home quietly. (RA calls, Explore and RA pages were friends-only
   * from 25 to 26 Oct 2026; open to members again with the RA platform, V063.)
   */
  friendsOnly?: boolean;
  /**
   * A Research Analyst's own tool (stored role "ra" or "owner"): anyone else
   * who opens the URL sees a short note instead of the server's refusal.
   */
  raOnly?: boolean;
  /**
   * Employees (V036 sales staff) may open this page. They see only such pages
   * (the sales console and their account); the server refuses the rest.
   */
  employees?: boolean;
  /**
   * A research tool that needs Investor Pro (V035): an RA's client (access
   * tier "client") doesn't see it in the nav, and opening the URL shows an
   * upgrade card. The server answers 402 `pro_required` on its own.
   */
  proOnly?: boolean;
  /**
   * Hidden from an RA's client (the session user's `ra_client`, even with
   * their own Investor Pro plan): their Research Analyst brought them, so
   * "Find an RA" isn't for them. Opening the URL shows a short note instead.
   */
  notForRaClients?: boolean;
}

/** "client" = an RA's client: their RA's calls and the basic market pages. */
export type AccessTier = "full" | "client";

export interface PageModule {
  pages: PageDef[];
  publicPages?: PageDef[];
}

export function collectPages(modules: Record<string, PageModule>): PageDef[] {
  const out: PageDef[] = [];
  const seen = new Map<string, string>();
  for (const [file, mod] of Object.entries(modules).sort(([a], [b]) =>
    a.localeCompare(b),
  )) {
    if (!Array.isArray(mod.pages)) {
      throw new Error(`${file} must export \`pages: PageDef[]\``);
    }
    for (const page of mod.pages) {
      if (!page.path.startsWith("/"))
        throw new Error(`${file}: path must start with "/": ${page.path}`);
      const other = seen.get(page.path);
      if (other)
        throw new Error(
          `duplicate page path ${page.path} in ${file} and ${other}`,
        );
      seen.set(page.path, file);
      out.push(page);
    }
  }
  return out;
}

const ROLE_ALIASES: Record<string, string> = {
  owner: "admin",
  subscriber: "investor",
};

/** The platform role for a stored role (mirrors rai.platform.roles). */
export function platformRole(role: string | undefined): string | undefined {
  return role === undefined ? undefined : (ROLE_ALIASES[role] ?? role);
}

function visibleTo(
  page: PageDef,
  role: string | undefined,
  aiCalls: boolean,
  tier: AccessTier,
  raClient: boolean,
): boolean {
  const effective = platformRole(role);
  if (effective === "employee" && !page.employees) return false;
  if (page.notForRaClients && raClient) return false;
  if (page.proOnly && tier === "client") return false;
  if (page.ownerOnly && effective !== "admin") return false;
  if (page.aiAccess && effective !== "admin" && !aiCalls) return false;
  if (page.friendsOnly && effective !== "admin" && !aiCalls) return false;
  if (
    page.roles &&
    !(
      role !== undefined &&
      (page.roles.includes(role) || page.roles.includes(effective ?? ""))
    )
  ) {
    return false;
  }
  return true;
}

const RA_ROLES = ["ra", "owner"];

/**
 * Why this user can't use a page, or null when they can: "admin" for admin
 * pages, "ai" for AI calls without an invitation, "ra" for an RA's tools.
 * The app shell shows a note instead of the page (the server refuses anyway).
 */
export function pageRefusal(
  page: Pick<
    PageDef,
    | "ownerOnly"
    | "aiAccess"
    | "raOnly"
    | "proOnly"
    | "employees"
    | "notForRaClients"
    | "friendsOnly"
  >,
  role: string | undefined,
  aiCalls: boolean,
  tier: AccessTier = "full",
  raClient = false,
): "admin" | "ai" | "ra" | "pro" | "employee" | "ra_client" | "hidden" | null {
  if (role === undefined) return null;
  const effective = platformRole(role);
  if (effective === "employee") return page.employees ? null : "employee";
  // switched off for this user: Home, with no note (one kind of account)
  if (page.friendsOnly && effective !== "admin" && !aiCalls) return "hidden";
  if (page.notForRaClients && raClient) return "ra_client";
  if (page.ownerOnly && effective !== "admin") return "admin";
  if (page.proOnly && tier === "client") return "pro";
  if (page.aiAccess && effective !== "admin" && !aiCalls) return "ai";
  if (page.raOnly && !RA_ROLES.includes(role)) return "ra";
  return null;
}

export function navItems(
  pages: PageDef[],
  role: string | undefined,
  aiCalls = false,
  tier: AccessTier = "full",
  raClient = false,
) {
  return pages
    .filter(
      (p): p is PageDef & { nav: NavEntry } =>
        !!p.nav && visibleTo(p, role, aiCalls, tier, raClient),
    )
    .sort(
      (a, b) =>
        NAV_GROUPS.indexOf(a.nav.group) - NAV_GROUPS.indexOf(b.nav.group) ||
        (a.nav.order ?? 100) - (b.nav.order ?? 100) ||
        a.nav.label.localeCompare(b.nav.label),
    );
}

const modules = import.meta.glob<PageModule>("../pages/*/index.tsx", {
  eager: true,
});

export const pages: PageDef[] = collectPages(modules);

/** Pages that work signed out, from modules that export `publicPages`. */
export const publicPages: PageDef[] = collectPages(
  Object.fromEntries(
    Object.entries(modules)
      .filter(([, mod]) => Array.isArray(mod.publicPages))
      .map(([file, mod]) => [file, { pages: mod.publicPages ?? [] }]),
  ),
);

/** Where "/" goes. */
export const HOME_PATH = "/app/home";
/** Where an employee's app starts (the sales console). */
export const EMPLOYEE_HOME = "/app/crm";
